import React, {useState} from 'react';

import {assert} from 'Asserts';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {ProcessOutputSubmitParams} from 'notebook/structured-tables/action-functions/submitProcessOutputsActionFunction';
import {
  CreateOrUpdateServerProcessOutput,
  UpdateServerProcessOutput,
} from 'processes/dao/ProcessManagementDAO';
import {SubmitOutputsConfirmationQuery} from 'processes/tables/__generated/SubmitOutputsConfirmationQuery';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';

import {CreateServerProcessOutput} from '../../dao/ProcessManagementDAO';
import CreateOutputsConfirmationPane from './CreateOutputsConfirmationPane';
import MultiStepSubmitOutputsConfirmationModal, {
  submitOutputsConfirmationUsesPanesWithoutTables,
} from './MultiStepSubmitOutputsConfirmationModal';
import UpdateMappedOutputsConfirmationPane from './UpdateMappedOutputsConfirmationPane';

type ProcessOutput = ExtractByTypename<
  SubmitOutputsConfirmationQuery['processOutputs'][number],
  'ProcessOutputNode'
>;

type ProcessTask = ExtractByTypename<
  SubmitOutputsConfirmationQuery['processTasks'][number],
  'ProcessTaskNode'
>;

interface SubmitOutputsConfirmationModalProps {
  outputsBeingCreated: Array<CreateServerProcessOutput>;
  outputsAlreadyMappedWithUpdatedFields: Array<UpdateServerProcessOutput>;
  allOutputsForServer: Array<CreateOrUpdateServerProcessOutput>;
  outputs: Array<ProcessOutput>;
  tasks: Array<ProcessTask>;
  onRequestClose: () => void;
  onRequestSubmit: (outputSubmitParams: ProcessOutputSubmitParams) => void;
}

export default function SubmitOutputsConfirmationModal(
  props: SubmitOutputsConfirmationModalProps
): JSX.Element {
  const [tasks, setTasks] = useState(props.tasks);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  const handleSubmit = (): void => {
    const taskSpecs =
      tasks.length > 0
        ? tasks.map(({id, status}) => ({
            id,
            statusId: status.id,
          }))
        : undefined;
    props.onRequestSubmit({processOutputs: props.allOutputsForServer, processTasks: taskSpecs});
  };

  assert(
    props.outputsBeingCreated.length > 0 === props.tasks.length > 0,
    'Can only provide tasks if outputs are being created'
  );
  assert(
    props.outputsAlreadyMappedWithUpdatedFields.length > 0 || props.outputsBeingCreated.length > 0,
    'Expected mapped outputs to be updated or outputs to be created'
  );

  if (props.outputsAlreadyMappedWithUpdatedFields.length > 0 && props.outputsBeingCreated.length > 0) {
    // If there are multiple confirmation steps we render a special multi step confirmation modal.
    return (
      <MultiStepSubmitOutputsConfirmationModal
        onRequestSubmit={handleSubmit}
        onRequestClose={props.onRequestClose}
        onUpdateTasks={setTasks}
        tasks={tasks}
        outputs={props.outputs}
        outputsBeingCreated={props.outputsBeingCreated}
        outputsAlreadyMappedWithUpdatedFields={props.outputsAlreadyMappedWithUpdatedFields}
      />
    );
  }

  const useLargeModal = !submitOutputsConfirmationUsesPanesWithoutTables(props);

  return (
    <ExpandableModal
      title="Submit outputs"
      expandHeight={useLargeModal}
      large={useLargeModal}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      buttons={[
        {
          text: 'Submit',
          style: ButtonStyle.PRIMARY,
          submit: true,
          disabled: !isSubmitEnabled,
        },
      ]}
      modalKey="submitProcessOutputs"
      rememberExpandedSetting={true}
    >
      {props.outputsAlreadyMappedWithUpdatedFields.length > 0 && (
        <UpdateMappedOutputsConfirmationPane
          outputs={props.outputs}
          outputsAlreadyMappedWithUpdatedFields={props.outputsAlreadyMappedWithUpdatedFields}
          onChangeAreQueriesCompleted={setIsSubmitEnabled}
        />
      )}
      {props.outputsBeingCreated.length > 0 && (
        <CreateOutputsConfirmationPane
          tasks={tasks}
          onUpdateTasks={setTasks}
          outputsBeingCreated={props.outputsBeingCreated}
          onChangeAreQueriesCompleted={setIsSubmitEnabled}
        />
      )}
    </ExpandableModal>
  );
}
